import { BaseDialog } from "@/lib/Components/Dialog/BaseDialog";
import { GenericForm } from "@/lib/Components/Form/Forms/GenericForm";
import { DialogSubmitButton } from "@/lib/Components/Dialog/Components/DialogSubmitButton";
import { GapRentalForm } from "@/app/GapRentals/Forms/GapRentalForm";
import { CreateRelocationInput } from "@/gql/graphql";
import { useDialogState } from "@/lib/Components/Dialog/Hooks/useDialogState";
import { gapRentalValidation } from "@/app/GapRentals/Validation/gapRentalValidation";
import {
  GapRentalFormValues,
  getDefaultGapRentalValues,
} from "@/app/GapRentals/Utils/getDefaultGapRentalValues";
import { toGapRentalInput } from "@/app/GapRentals/Utils/gapRentalDistance";
import {
  useGqlMutation,
  useNullableSuspenseGqlQuery,
} from "@/lib/GraphQLCodegen/fetcher";
import { createRelocationMutation } from "@/app/Relocations/GraphQL/relocationMutations";
import { supplierRecordQuery } from "@/app/Suppliers/GraphQL/supplierRecordQuery";
import { useGlobalSupplier } from "@/app/Suppliers/Utils/useGlobalSupplier";
import { toast } from "sonner";
import { useNavigate } from "@tanstack/react-router";
import { toFormikValidationSchema } from "@/lib/Zod/zod-formik-adapter";
import { FormikProps } from "formik";
import { useCallback, useRef } from "react";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { ConfirmCloseDialog } from "@/lib/Components/Dialog/ConfirmCloseDialog";

export function GapRentalCreateDialog({
  initialSupplierId,
}: {
  initialSupplierId?: string;
}) {
  const navigate = useNavigate();
  const { mutateAsync: createRelocation } = useGqlMutation(
    createRelocationMutation,
  );
  const { close, reject } = useDialogState();
  const { defaultSupplierId } = useGlobalSupplier();
  const id = initialSupplierId ?? defaultSupplierId;
  const { data } = useNullableSuspenseGqlQuery(
    supplierRecordQuery,
    {
      id: id!,
    },
    !!id,
  );

  const formikRef = useRef<FormikProps<GapRentalFormValues>>(null);
  const { open: openConfirmDialog } = useDialog(ConfirmCloseDialog);

  const handleClose = useCallback(async () => {
    if (formikRef.current?.dirty) {
      openConfirmDialog({
        onConfirm: async () => {
          reject?.("dialog closed");
          await close();
        },
      });
    } else {
      reject?.("dialog closed");
      await close();
    }
  }, [openConfirmDialog, close, reject]);

  const initialSupplier = data?.supplier;

  return (
    <BaseDialog title="Add gap rental" onClose={handleClose}>
      <GenericForm<GapRentalFormValues>
        innerRef={formikRef}
        validationSchema={toFormikValidationSchema(gapRentalValidation)}
        initialValues={getDefaultGapRentalValues({
          supplier: initialSupplier,
        })}
        onSubmit={async (values) => {
          const { deliveryOfficeIds, ...rest } = toGapRentalInput(values);
          const [firstOfficeId] = deliveryOfficeIds;

          const input: CreateRelocationInput = {
            ...rest,
            deliveryOffice: {
              connect: firstOfficeId,
            },
          };

          const res = await createRelocation({ input });

          toast.success(`${res.createRelocation.reference} created`, {
            action: {
              label: "View",
              onClick: () => {
                return navigate({
                  from: "/$tenant/$orgId/supplier/{-$supplierId}",
                  to: "/$tenant/$orgId/supplier/{-$supplierId}/gap-rentals/$relocationId",
                  params: (prev) => ({
                    ...prev,
                    relocationId: res.createRelocation.id,
                  }),
                });
              },
            },
          });

          await close();
        }}
      >
        <GapRentalForm showInclusions />
        <DialogSubmitButton text="Add gap rental" />
      </GenericForm>
    </BaseDialog>
  );
}
